import { InjectionToken } from '@angular/core';
import { QuestionnaireStatus } from '../constants/fhir.constants';
import { FHIRExtension, FHIRInitialValue, FHIRItem, FHIRQuestionnaire } from '../models/fhir.model';
import { IPropertyDefinition } from './metadata-provider.interface';

// Re-export FHIRInitialValue from models for convenience
export type { FHIRInitialValue };

// ============================================================================
// CONFIGURATION INTERFACES
// ============================================================================

/** Extension URL configuration - allows customization of URN prefixes */
export interface FHIRExtensionConfig {
  /** Base prefix for all extensions (default: 'urn:ccs:ext') */
  prefix: string;
  /** UI config extension key (default: 'ui') */
  uiConfig: string;
  /** Questionnaire URL prefix (default: 'urn:ccs:q') */
  questionnairePrefix: string;
  /** Publisher identifier */
  publisher: string;
}

/** Injection token for custom FHIR configuration */
export const FHIR_CONVERTER_CONFIG = new InjectionToken<Partial<FHIRExtensionConfig>>('FHIR_CONVERTER_CONFIG');

// ============================================================================
// CONTROL CARD INTERFACE (Designer UI)
// ============================================================================

/** Interface for designer control cards used in FHIR conversion */
export interface IControlCard {
  id: string;
  controlType: string; // Type of control (text-input, radio-group, etc.) from the registry
  type?: string; // Runtime property (e.g., HTML input type: text, email, password)
  icon: string;
  question: string;
  property?: string; // Data property/field name for binding
  description?: string; // Help text description
  placeholder?: string; // Input placeholder text
  isRequired: boolean;
  hasOptions: boolean;
  optionCount?: number;
  hasOther?: boolean;
  /** True when this control is the auto-generated "Other" text-input subcontrol (internal, not user-authored). */
  __isOtherInput?: boolean;
  /** LinkId of this control's auto-generated "Other" text-input subcontrol child, once synthesized/reconstructed. */
  otherInputLinkId?: string;
  options?: Array<{ label: string; value?: string; code?: string; display?: string }>;
  validationErrors?: string[];
  children?: IControlCard[];
  label?: string;
  component?: any; // Angular component type (Type<any>) - builder wrapper component for designer canvas
  componentProps?: any; // Component input properties
  staticData?: Record<string, any>; // Static data for preview/design mode (not editable in builder)
  fhirPath?: string; // Optional FHIRPath for data binding (can be used by renderers to bind to specific data properties)
  tag?: string[];
  [key: string]: unknown;
}

// ============================================================================
// CONVERSION OPTIONS & RESULTS
// ============================================================================

/** Conversion options for fine-grained control */
export interface ConversionOptions {
  /** Include metadata extensions (default: true) */
  includeMetadata?: boolean;
  /** Include empty arrays (default: false) */
  includeEmptyArrays?: boolean;
  /** Pretty print JSON output (default: true) */
  prettyPrint?: boolean;
  /** Validate output against FHIR spec (default: false) */
  validate?: boolean;
  /** Custom extension transformer */
  extensionTransformer?: (ext: FHIRExtension) => FHIRExtension;
  /** Custom item transformer */
  itemTransformer?: (item: FHIRItem) => FHIRItem;
}

/** Conversion result with metadata */
export interface ConversionResult<T> {
  success: boolean;
  data?: T;
  errors: ConversionError[];
  warnings: string[];
  metrics: ConversionMetrics;
}

/** Conversion error details */
export interface ConversionError {
  code: string;
  message: string;
  path?: string;
  controlId?: string;
  severity: 'error' | 'warning';
}

/** Performance metrics for conversion */
export interface ConversionMetrics {
  totalItems: number;
  sectionsCount: number;
  subsectionsCount: number;
  controlsCount: number;
  extensionsCount: number;
  durationMs: number;
  jsonSizeBytes?: number;
}

/** Batch conversion progress callback */
export type ProgressCallback = (progress: { current: number; total: number; item?: string }) => void;

// ============================================================================
// FHIR VALUE TYPES - Re-exported from models
// ============================================================================

// Note: FHIRInitialValue is imported from models/fhir.model.ts

// ============================================================================
// SERVICE INTERFACES
// ============================================================================

/** Designer questionnaire generation options */
export interface DesignerQuestionnaireOptions {
  id: string;
  title: string;
  icon?: string;
  controls: IControlCard[];
  sections?: Array<{
    id?: string;
    title: string;
    icon?: string;
    isVisible?: boolean;
    pdfExport?: boolean;
    aiExtraction?: boolean;
    amend?: boolean;
    sectionRequired?: boolean;
    controls: IControlCard[];
    assessmentStrategy?: string | null;
    assessmentPosition?: string | null;
    questionTitle?: string;
    tag?: string[];
  }>;
  status?: QuestionnaireStatus;
  assessmentStrategy?: string | null;
  assessmentPosition?: string | null;
  sectionId?: string;
  questionTitle?: string;
  aiExtraction?: boolean;
  amend?: boolean;
  sectionRequired?: boolean;
  tag?: string[];
}

/** Interface for Designer to FHIR conversion */
export interface IDesignerFhirConverter {
  /** Convert a single designer control to a FHIR item */
  convertControl(control: IControlCard, index: number, parentPath: string): FHIRItem | null;

  /** Convert array of controls to FHIR items */
  convertControls(controls: IControlCard[], parentPath: string): FHIRItem[];

  /** Generate complete FHIR Questionnaire from designer controls */
  generateQuestionnaire(options: DesignerQuestionnaireOptions): string;

  /** Convert with detailed result including errors and metrics */
  convertWithResult(
    options: DesignerQuestionnaireOptions,
    conversionOptions?: ConversionOptions
  ): ConversionResult<string>;
}

/** Interface for Template ↔ FHIR bidirectional conversion */
export interface ITemplateFhirConverter<TTemplate = unknown> {
  fhirToBuilderTemplate(questionnaire: FHIRQuestionnaire, template: TTemplate): TTemplate;
}

/** Interface for FHIR validation */
export interface IFhirValidator {
  /**
   * Validate a FHIR Questionnaire
   *
   * @returns True if valid, false otherwise
   */
  validate(questionnaire: unknown, errors?: ConversionError[], warnings?: string[]): boolean;

  /** Validate FHIR items recursively */
  validateItems(items: FHIRItem[], errors: ConversionError[], warnings: string[], parentPath: string): void;
}

/** Interface for FHIR extension building */
export interface IFhirExtensionBuilder<TTemplate = unknown, TSection = unknown, TControl = unknown> {
  /** Build section-level extensions */
  buildSectionExtensions(
    icon?: string,
    assessmentStrategy?: string | null,
    assessmentPosition?: string | null,
    sectionId?: string,
    required?: boolean,
    questionTitle?: string,
    isVisible?: boolean,
    pdfExport?: boolean,
    aiExtraction?: boolean,
    amend?: boolean,
    tag?: string[],
    sectionRequired?: boolean
  ): FHIRExtension[];

  /** Build control-level ui-config extension */
  buildControlExtensions(control: IControlCard, metadata: ControlMetadataInfo | null): FHIRExtension[];

  /** Build template-level extensions */
  buildTemplateExtensions?(template: TTemplate): FHIRExtension[];

  /** Build section extensions for template conversion */
  buildTemplateSectionExtensions?(section: TSection): FHIRExtension[];

  /** Build control extensions for template conversion */
  buildTemplateControlExtensions?(control: TControl): FHIRExtension[];
}

/** Simplified metadata info passed to extension builder */
export interface ControlMetadataInfo {
  componentType?: string;
  package?: string;
  category?: string;
  isLayoutControl?: boolean;
  fhirType?: string;
  label?: string;
  supportsOptions?: boolean;
  propertyDefinitions?: IPropertyDefinition[];
}

/** Interface for LinkId generation */
export interface ILinkIdGenerator {
  /** Generate a unique 3-character slug */
  generateSlug(): string;

  /** Generate a linkId from title with unique suffix */
  generateFromTitle(title: string): string;

  /** Generate a UUID v4 */
  generateUUID(): string;

  /** Generate control linkId based on type and parent path */
  generateControlLinkId(controlType: string, parentPath: string, isLayout: boolean, displayText?: string): string;
}

/** Main FHIR Converter Service Interface (Facade) */
export interface IFhirConverterService<TTemplate = unknown> {
  // Configuration
  getConfig(): Readonly<FHIRExtensionConfig>;
  clearCache(): void;

  // Designer conversion
  convertControlToFhirItem(control: IControlCard, index: number, parentPath?: string): FHIRItem | null;
  convertDesignerControlsToFhirItems(controls: IControlCard[], parentPath?: string): FHIRItem[];
  generateDesignerFhirQuestionnaire(options: DesignerQuestionnaireOptions): string;
  convertToFhirWithResult(
    options: DesignerQuestionnaireOptions,
    conversionOptions?: ConversionOptions
  ): ConversionResult<string>;

  // Template conversion
  fhirToBuilderTemplate(questionnaire: FHIRQuestionnaire, template: TTemplate): TTemplate;
  // Batch operations
  batchConvert(
    templates: DesignerQuestionnaireOptions[],
    options?: ConversionOptions,
    onProgress?: ProgressCallback
  ): Promise<ConversionResult<string>[]>;

  // Validation
  validateQuestionnaire(questionnaire: unknown, errors?: ConversionError[], warnings?: string[]): boolean;
}
